import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid'
import { useDropzone } from 'react-dropzone'
import { useSelector } from 'react-redux'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { toast } from 'sonner'
import {
  uploadFilesThunk,
  deleteFileThunk,
  deleteAllDocumentsThunk,
} from '@/redux/asyncThunks/configuration'
import { RootState } from '@/redux/store'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { useState, useCallback } from 'react'
import { MyFile, DocumentRAGSettings } from '@/redux/types/configuration'
import { setUploadedFiles } from '@/redux/slices/configurationSlice'
import { GameRoomStatus } from '@/redux/types/room'
import {
  TrashIcon,
  Loader2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
} from 'lucide-react'
import { LoadingOverlay } from './LoadingOverlay'
import { Label } from '@radix-ui/react-dropdown-menu'
import {
  formatFileSize,
  validateFileSize,
  validateFileType,
  validateTotalFileSize,
  ACCEPTED_FILE_TYPES,
} from '@/utils/fileValidation'
import { getFileIconPath } from '@/utils/fileIcons'
import { cn } from '@/lib/utils'
import DocumentRAGSettingsComponent from './DocumentRAGSettings'
import { Badge } from '@/components/ui/badge'
import { reprocessDocuments } from '@/api/configuration'
import { reprocessRoomDocuments } from '@/api/roomAdvisorConfig'
import DocumentReprocessButton from './DocumentReprocessButton'
import ConfirmationModal from '@/components/OGE/OGEDeletionModal'

interface ConfigurationFormProps {
  chatbotid: string
  disabled: boolean
  advisorRAGDefaults?: {
    ragEnabled: boolean
    ragTopK: number
    ragChunkSize: number
    ragChunkOverlap: number
    ragSimilarityThreshold: number
    ragMaxContextLength: number
  }
  uploadTarget?: 'advisor' | 'room'
  | 'dummy_bot'
  roomId?: string
}

export default function FileUpload({
  chatbotid,
  disabled,
  advisorRAGDefaults = {
    ragEnabled: true,
    ragTopK: 5,
    ragChunkSize: 1000,
    ragChunkOverlap: 200,
    ragSimilarityThreshold: 0.2,
    ragMaxContextLength: 3000,
  },
  uploadTarget = 'advisor',
  roomId,
}: ConfigurationFormProps) {
  const dispatch = useAppDispatch()
  const { files: uploadedFiles = [] } = useSelector(
    (state: RootState) => state.configuration
  )

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [isDeletingFile, setIsDeletingFile] = useState(false)
  const [deletingFileId, setDeletingFileId] = useState<number | null>(null)
  const [uploadProgress, setUploadProgress] = useState('')
  const [expandedFiles, setExpandedFiles] = useState<Set<number>>(new Set())
  const [isReprocessing, setIsReprocessing] = useState(false)
  const [reprocessingFileId, setReprocessingFileId] = useState<number | null>(
    null
  )
  const [showReprocessConfirmModal, setShowReprocessConfirmModal] =
    useState(false)
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false)
  const [isDeletingAll, setIsDeletingAll] = useState(false)
  const { currentRoom } = useAppSelector((state: RootState) => state.room)
  const isGameCompleted = currentRoom?.status === GameRoomStatus.COMPLETED

  // "Delete All" is offered for advisor + world-setup (room) scopes only. AI
  // player docs are cleared via the whole-room control (issue #563).
  const deleteAllScope: 'advisor' | 'world-setup' | null =
    uploadTarget === 'advisor'
      ? 'advisor'
      : uploadTarget === 'room'
        ? 'world-setup'
        : null

  const deleteAllLabel =
    deleteAllScope === 'world-setup'
      ? 'all World Setup documents'
      : 'all documents for this advisor'

  const handleConfirmDeleteAll = async () => {
    setIsDeletingAll(true)
    try {
      const result = await dispatch(
        deleteAllDocumentsThunk(
          deleteAllScope === 'world-setup'
            ? { scope: 'world-setup', roomId }
            : { scope: 'advisor', chatbotId: chatbotid }
        )
      ).unwrap()
      dispatch(setUploadedFiles([]))
      toast.success(
        `Deleted ${result.documents_deleted} document(s)` +
          (result.documents_unlinked
            ? `, unlinked ${result.documents_unlinked} shared elsewhere`
            : '')
      )
      setShowDeleteAllConfirm(false)
    } catch (error) {
      toast.error(`Failed to delete documents: ${error}`)
    } finally {
      setIsDeletingAll(false)
    }
  }

  const handleDocumentSettingsChange = (
    documentId: number,
    settings: DocumentRAGSettings
  ) => {
    const updatedFiles = uploadedFiles.map((file) =>
      file.id === documentId ? { ...file, rag_settings: settings } : file
    )
    dispatch(setUploadedFiles(updatedFiles))
  }

  const toggleFileExpansion = (fileId: number) => {
    setExpandedFiles((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(fileId)) {
        newSet.delete(fileId)
      } else {
        newSet.add(fileId)
      }
      return newSet
    })
  }

  const getFileStatusBadge = (file: MyFile) => {
    if (file.rag_settings?.use_full_context) {
      return <Badge variant='outline'>Full Context</Badge>
    }
    if (file.rag_settings) {
      const hasCustomSettings = Object.values(file.rag_settings).some(
        (val) => val !== null && val !== undefined && val !== false
      )
      if (hasCustomSettings) {
        return <Badge variant='secondary'>Custom Settings</Badge>
      }
    }
    return <Badge variant='outline'>RAG (Default)</Badge>
  }

  const handleReprocessDocument = useCallback(
    async (documentId: number) => {
      if (isReprocessing) return
      if (uploadTarget === 'room' && !roomId) {
        toast.error('Room ID is missing; cannot reprocess.')
        return
      }

      setIsReprocessing(true)
      setReprocessingFileId(documentId)

      try {
        const response =
          uploadTarget === 'room' && roomId
            ? await reprocessRoomDocuments(roomId, [documentId])
            : await reprocessDocuments(
                chatbotid,
                [documentId],
                uploadTarget === 'dummy_bot' ? 'dummy_bot' : 'advisor'
              )

        if (response.success && response.results.length > 0) {
          const result = response.results[0]
          if (result.status === 'success') {
            toast.success(
              `Document "${result.document_name}" reprocessed successfully. ${result.chunks_created || 0} chunks created.`
            )
          } else if (result.status === 'skipped') {
            toast.info(
              `Document "${result.document_name}" skipped: ${result.reason || 'Unknown reason'}`
            )
          } else {
            toast.error(
              `Failed to reprocess "${result.document_name}": ${result.error || 'Unknown error'}`
            )
          }
        } else {
          toast.error('Failed to reprocess document')
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error'
        toast.error(`Error reprocessing document: ${errorMessage}`)
      } finally {
        setIsReprocessing(false)
        setReprocessingFileId(null)
      }
    },
    [chatbotid, isReprocessing, uploadTarget, roomId]
  )

  const handleReprocessAll = useCallback(() => {
    if (uploadTarget === 'room' && !roomId) {
      toast.error('Room ID is missing; cannot reprocess.')
      return
    }
    if (isReprocessing) return

    // Filter out full context files
    const filesToReprocess = uploadedFiles.filter(
      (file) => !file.rag_settings?.use_full_context
    )

    if (filesToReprocess.length === 0) {
      toast.info('No documents to reprocess. Full context files are skipped.')
      return
    }

    // Show confirmation modal instead of browser confirm
    setShowReprocessConfirmModal(true)
  }, [uploadedFiles, isReprocessing, uploadTarget, roomId])

  const handleConfirmReprocessAll = useCallback(async () => {
    setShowReprocessConfirmModal(false)

    // Filter out full context files
    const filesToReprocess = uploadedFiles.filter(
      (file) => !file.rag_settings?.use_full_context
    )

    setIsReprocessing(true)

    try {
      const documentIds = filesToReprocess.map((file) => file.id)
      const response =
        uploadTarget === 'room' && roomId
          ? await reprocessRoomDocuments(roomId, documentIds)
          : await reprocessDocuments(
              chatbotid,
              documentIds,
              uploadTarget === 'dummy_bot' ? 'dummy_bot' : 'advisor'
            )

      if (response.success) {
        const successCount = response.processed
        const skippedCount = response.skipped
        const failedCount = response.failed

        // Show summary toast
        if (failedCount === 0 && skippedCount === 0) {
          // All succeeded
          toast.success(`Successfully reprocessed ${successCount} document(s)`)
        } else if (failedCount === 0) {
          // Some succeeded, some skipped
          toast.success(
            `Reprocessing complete: ${successCount} succeeded, ${skippedCount} skipped`
          )
        } else {
          // Some failed
          const summary = []
          if (successCount > 0) summary.push(`${successCount} succeeded`)
          if (skippedCount > 0) summary.push(`${skippedCount} skipped`)
          if (failedCount > 0) summary.push(`${failedCount} failed`)

          toast.error(
            `Reprocessing completed with issues: ${summary.join(', ')}`
          )

          // Log failed documents for debugging
          const failedDocs = response.results.filter(
            (r) => r.status === 'failed'
          )
          if (failedDocs.length > 0) {
            console.error('Failed documents:', failedDocs)
          }
        }
      } else {
        toast.error('Failed to reprocess documents')
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      toast.error(`Error reprocessing documents: ${errorMessage}`)
    } finally {
      setIsReprocessing(false)
    }
  }, [chatbotid, uploadedFiles, uploadTarget, roomId])

  const getReprocessableCount = () =>
    uploadedFiles.filter((file) => !file.rag_settings?.use_full_context).length

  const { getRootProps, getInputProps } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    onDrop: (acceptedFiles) => {
      if (isUploading) return

      const validFiles = acceptedFiles.filter((file) => {
        return validateFileType(file) && validateFileSize(file)
      })
      if (validFiles.length === 0) return

      const newTotalFiles = [...selectedFiles, ...validFiles]
      if (!validateTotalFileSize(newTotalFiles)) {
        return
      }

      setSelectedFiles(newTotalFiles)
    },
    onDropRejected: (fileRejections) => {
      fileRejections.forEach(({ file, errors }) => {
        errors.forEach((error) => {
          if (error.code === 'file-invalid-type') {
            toast.error(
              `File "${file.name}" is not supported. Supported formats: PDF and text files.`
            )
          } else if (error.code === 'file-too-large') {
            toast.error(`File "${file.name}" exceeds the 40MB size limit.`)
          } else {
            toast.error(`File "${file.name}" was rejected: ${error.message}`)
          }
        })
      })
    },
    disabled: isUploading || isGameCompleted,
  })

  const handleClearFiles = () => {
    if (isGameCompleted || isUploading) return
    setSelectedFiles([])
  }

  const handleUploadFiles = () => {
    if (isGameCompleted || isUploading) return

    if (selectedFiles.length === 0) {
      toast.error('No files to upload')
      return
    }

    const allFilesValid = selectedFiles.every(
      (file) => validateFileType(file) && validateFileSize(file)
    )
    if (!allFilesValid || !validateTotalFileSize(selectedFiles)) {
      return
    }

    setIsUploading(true)
    setUploadProgress('Uploading...')

    const directory = ''
    dispatch(
      uploadFilesThunk({
        files: selectedFiles,
        directory,
        chatbotid,
        target: uploadTarget,
        roomId,
      })
    )
      .unwrap()
      .then(() => {
        setUploadProgress('Upload completed successfully!')
        setTimeout(() => {
          toast.success('Files uploaded successfully!')
          setSelectedFiles([])
        }, 500)
      })
      .catch((error) => {
        // The thunk rejects with a string message (rejectWithValue), so read it
        // directly — doing `error.message` on a string always yielded the
        // useless "Unknown error". (#561)
        const message =
          typeof error === 'string'
            ? error
            : error?.message || 'Unknown error'
        toast.error(`Upload failed: ${message}`)
      })
      .finally(() => {
        setTimeout(() => {
          setIsUploading(false)
          setUploadProgress('')
        }, 1000)
      })
  }

  const handleDeleteFile = (fileId: number) => {
    if (isGameCompleted || isUploading) return

    setIsDeletingFile(true)
    setDeletingFileId(fileId)

    const deletePayload =
      uploadTarget === 'room' && roomId
        ? { fileId, target: 'room' as const, roomId }
        : uploadTarget === 'dummy_bot'
          ? { fileId, chatbotId: chatbotid, target: 'dummy_bot' as const }
          : { fileId, chatbotId: chatbotid }

    dispatch(deleteFileThunk(deletePayload))
      .unwrap()
      .then(() => {
        toast.success('File deleted successfully!')
        dispatch(
          setUploadedFiles(uploadedFiles.filter((file) => file.id !== fileId))
        )
      })
      .catch((error) => {
        toast.error(`Failed to delete file: ${error}`)
      })
      .finally(() => {
        setIsDeletingFile(false)
        setDeletingFileId(null)
      })
  }

  const getTotalSelectedFileSize = () => {
    return selectedFiles.reduce((sum, file) => sum + file.size, 0)
  }

  return (
    <div>
      <LoadingOverlay
        isVisible={isUploading}
        message={uploadProgress || 'Uploading files...'}
      />
      <div className='space-y-6'>
        <h2 className='text-2xl font-semibold text-foreground'>Upload Files</h2>
        <div className='space-y-4'>
          <div className='flex gap-2 items-center'>
            <div className='flex gap-2 items-center'>
              <Label className='text-sm font-medium'>File</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <QuestionMarkCircleIcon className='w-4 h-4 cursor-help text-muted-foreground' />
                </TooltipTrigger>
                <TooltipContent className='max-w-xs'>
                  Upload your configuration files here
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          <div
            {...getRootProps()}
            className={cn(
              'p-8 space-y-4 text-center border-2 border-dashed rounded-lg transition-all',
              isGameCompleted || isUploading || disabled
                ? 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer hover:border-blue-400'
            )}
          >
            <input
              {...getInputProps()}
              disabled={isGameCompleted || isUploading || disabled}
            />
            {selectedFiles.length === 0 ? (
              <div className='flex flex-row justify-center'>
                <img
                  src='/icons/folder-plus.svg'
                  alt='upload'
                  className='w-14 h-14 text-input-field-blue'
                />
              </div>
            ) : (
              <div className='flex flex-wrap gap-4 space-y-2'>
                {selectedFiles.map((file, index) => (
                  <div key={index} className='flex flex-row gap-2 items-center'>
                    <img
                      src={getFileIconPath(file.name)}
                      alt='file icon'
                      className='w-10 h-10'
                    />
                    <span className='text-sm truncate max-w-[150px]'>
                      {file.name}
                    </span>
                    <span className='text-xs text-muted-foreground'>
                      {formatFileSize(file.size)}
                    </span>
                    <button
                      onClick={(e) => {
                        if (isGameCompleted || isUploading) return
                        e.stopPropagation()
                        setSelectedFiles((prev) =>
                          prev.filter((_, i) => i !== index)
                        )
                      }}
                      className='text-red-500 hover:text-red-700'
                      disabled={isGameCompleted || isUploading}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div>
              <p className='text-sm text-muted-foreground'>
                {isUploading
                  ? 'Upload in progress...'
                  : 'Drop your files here or '}
                {!isUploading && (
                  <span className='cursor-pointer text-blue'>browse</span>
                )}
              </p>
              <p className='mt-1 text-xs text-muted-foreground'>
                Supported formats: PDF, TXT, MD, CSV, JSON, XML, HTML, and other
                text files
              </p>
              <p className='mt-1 text-xs text-muted-foreground'>
                Maximum total size: 40 MB
              </p>
              {selectedFiles.length > 0 && (
                <p className='mt-1 text-xs text-blue-600'>
                  Selected: {formatFileSize(getTotalSelectedFileSize())} / 40 MB
                </p>
              )}
            </div>
          </div>

          {!isGameCompleted && (
            <div className='flex gap-2 justify-end'>
              <Button
                type='button'
                size='lg'
                onClick={handleClearFiles}
                className='bg-dark-blue hover:bg-dark-blue-hovered'
                disabled={selectedFiles.length === 0 || isUploading}
              >
                Clear
              </Button>
              <Button
                type='button'
                size='lg'
                onClick={handleUploadFiles}
                className='bg-dark-blue hover:bg-dark-blue-hovered'
                disabled={selectedFiles.length === 0 || isUploading}
              >
                {isUploading ? (
                  <div className='flex gap-2 items-center'>
                    <Loader2 className='w-4 h-4 animate-spin' />
                    Uploading...
                  </div>
                ) : (
                  'Upload'
                )}
              </Button>
            </div>
          )}
        </div>
        <div className='space-y-4'>
          <div className='flex justify-between items-center'>
            <h2 className='text-lg font-semibold text-foreground'>
              Uploaded Files
            </h2>
            <div className='flex gap-2 items-center'>
            {uploadedFiles.length > 0 && deleteAllScope && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => setShowDeleteAllConfirm(true)}
                      disabled={
                        disabled ||
                        isReprocessing ||
                        isUploading ||
                        isDeletingFile ||
                        isDeletingAll ||
                        isGameCompleted
                      }
                      className='flex gap-2 items-center text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200'
                    >
                      {isDeletingAll ? (
                        <>
                          <Loader2 className='w-4 h-4 animate-spin' />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <TrashIcon className='w-4 h-4' />
                          Delete All ({uploadedFiles.length})
                        </>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Delete {deleteAllLabel} and their embeddings. This cannot be undone.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {uploadedFiles.length > 0 && getReprocessableCount() > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={handleReprocessAll}
                      disabled={
                        disabled ||
                        isReprocessing ||
                        isUploading ||
                        isDeletingFile ||
                        isGameCompleted
                      }
                      className='flex gap-2 items-center'
                    >
                      {isReprocessing ? (
                        <>
                          <Loader2 className='w-4 h-4 animate-spin' />
                          Reprocessing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className='w-4 h-4' />
                          Reprocess All ({getReprocessableCount()})
                        </>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      Reprocess all documents with current chunk settings. Full
                      context files will be skipped.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            </div>
          </div>
          {uploadedFiles.length > 0 ? (
            <div className='space-y-3'>
              {uploadedFiles.map((file: MyFile) => {
                const isDeleting = isDeletingFile && deletingFileId === file.id
                const isAnyFileDeleting = isDeletingFile
                const isExpanded = expandedFiles.has(file.id)
                return (
                  <div
                    key={file.id}
                    className={cn(
                      'border rounded-lg p-4 space-y-3',
                      isDeleting && 'opacity-50'
                    )}
                  >
                    <div className='flex justify-between items-center'>
                      <div className='flex flex-1 gap-3 items-center'>
                        <button
                          type='button'
                          onClick={() => toggleFileExpansion(file.id)}
                          className='text-muted-foreground hover:text-foreground'
                          disabled={
                            disabled || isAnyFileDeleting || isUploading
                          }
                        >
                          {isExpanded ? (
                            <ChevronDown className='w-4 h-4' />
                          ) : (
                            <ChevronRight className='w-4 h-4' />
                          )}
                        </button>
                        <span className='text-sm font-medium'>
                          {file.file_name}
                        </span>
                        <span className='text-xs text-muted-foreground'>
                          {file.file_size} KB
                        </span>
                        {getFileStatusBadge(file)}
                      </div>
                      <div className='flex gap-2 items-center'>
                        {!file.rag_settings?.use_full_context && (
                          <DocumentReprocessButton
                            documentId={file.id}
                            documentName={file.file_name}
                            onReprocess={handleReprocessDocument}
                            disabled={
                              disabled ||
                              isGameCompleted ||
                              isAnyFileDeleting ||
                              isUploading ||
                              isReprocessing
                            }
                          />
                        )}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type='button'
                                variant='ghost'
                                size='sm'
                                onClick={() => handleDeleteFile(file.id)}
                                className='text-red-500 hover:text-red-700 hover:bg-red-50'
                                disabled={
                                  isGameCompleted ||
                                  isAnyFileDeleting ||
                                  isUploading ||
                                  disabled ||
                                  (isReprocessing &&
                                    reprocessingFileId === file.id)
                                }
                              >
                                {isDeleting ? (
                                  <Loader2 className='w-4 h-4 animate-spin' />
                                ) : (
                                  <TrashIcon className='w-4 h-4' />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                {isDeleting ? 'Deleting...' : 'Delete file'}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className='pt-2 pl-7'>
                        <DocumentRAGSettingsComponent
                          documentId={file.id}
                          settings={file.rag_settings}
                          advisorDefaults={advisorRAGDefaults}
                          onSettingsChange={handleDocumentSettingsChange}
                          disabled={
                            disabled || isAnyFileDeleting || isUploading
                          }
                          roomScoped={uploadTarget === 'room'}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <p className='text-sm text-muted-foreground'>
              No files uploaded yet.
            </p>
          )}
        </div>
      </div>
      <ConfirmationModal
        isOpen={showReprocessConfirmModal}
        onClose={() => setShowReprocessConfirmModal(false)}
        onConfirm={handleConfirmReprocessAll}
        title='Reprocess Documents'
        description={`Reprocess ${getReprocessableCount()} document(s)? This may take a few moments. Full context files will be skipped.`}
        confirmButtonText='Reprocess'
        cancelButtonText='Cancel'
      />
      <ConfirmationModal
        isOpen={showDeleteAllConfirm}
        onClose={() => setShowDeleteAllConfirm(false)}
        onConfirm={handleConfirmDeleteAll}
        title='Delete all documents?'
        description={`This permanently deletes ${deleteAllLabel} (${uploadedFiles.length} file(s)) and their embeddings. Documents shared elsewhere are unlinked, not destroyed. This cannot be undone.`}
        confirmButtonText='Delete All'
        cancelButtonText='Cancel'
      />
    </div>
  )
}
